import { sessionStorage } from '@rategravity/frontend/modules/storage';
import { event } from '@rategravity/frontend/modules/user-actions';
import fetch from 'node-fetch';
import React from 'react';
import { animateScroll } from 'react-scroll';
import { permittedStates } from '../../decks/branches/state-verification';
import { getAuthToken } from '../../modules/authenticate';
import {
  CAPACITY_PATH,
  CASH_OUT_PATH,
  CONSULT_CONSTANT,
  CONSULT_PATH,
  LOW_CREDIT_SCORE_PATH,
  NEW_CONSTRUCTION_PATH,
  NON_PITCH,
  TOO_FAR_OUT_PATH,
  TOO_SOON_PATH,
  TRADELINE_ISSUE_PATH
} from '../../modules/consult-paths';
import {
  activateExperiment,
  EXPERIMENT_TEXT_RATES,
  EXPERIMENT_TEXT_RATES_ACTIVATE,
  getExperimentValue
} from '../../modules/experiments/optimize';
import parseName from '../../modules/parse-name';
import { pipeConsultPitch } from '../../modules/pipe-consult-pitch';
import { referralRockConversion } from '../../modules/referral-rock';
import ComparisonDashboard from '../../products/shared/comparison-dashboard';
import ConsultDashboard from '../../products/shared/consult/dashboard';
import NewConstructionDashboard from '../../products/shared/distant-new-construction-dashboard';
import GenericDashboard from '../../products/shared/generic-dashboard';
import LowCreditDashboard from '../../products/shared/low-credit-dashboard';
import { NonPitchDashboard } from '../../products/shared/non-pitch-dashboard';
import Complete from '../complete';
import Steps from '../steps';

const DefaultMessageHeader = ({ children, ...props }) => {
  return (
    <h2 style={{ fontSize: 36 }} {...props}>
      {children}
    </h2>
  );
};

const getMessage = (scenario, firstName, savings, state, vaStatus) => {
  if (!permittedStates.includes(state) && vaStatus !== 'Ineligible') {
    return (
      <div>
        <h1>Hi {firstName} - Unfortunately, we’re not licensed in your state</h1>
        <br />
        <h4>
          However, we can offer you advice on what rate you should expect and may be able to connect
          you with one of our lenders. Set up a call with one of our advisors to learn more.
        </h4>
      </div>
    );
  }
  switch (scenario) {
    case 'Affordability':
      return (
        <DefaultMessageHeader>
          Hey {firstName}, set up a 7-minute review call with your advisor so they can walk you
          through our analysis.
        </DefaultMessageHeader>
      );
    case 'Preapproval':
      return (
        <DefaultMessageHeader>
          Hey {firstName}, set up a 7-minute review call with your advisor so they can walk you
          through your pre-approval options.
        </DefaultMessageHeader>
      );
    case 'Purchase':
    case 'Qualify':
      return (
        <DefaultMessageHeader>
          Hey {firstName}, you can save ${savings} annually with Own Up vs other lenders. Set up a 7
          minute call to discuss your options.
        </DefaultMessageHeader>
      );
    case 'RefiFull':
    case 'RefiQualify':
      return (
        <DefaultMessageHeader>
          Hey {firstName}, we&apos;re preparing your quote! Set up a 7 minute call to review your
          options.
        </DefaultMessageHeader>
      );
  }
};

/**
 * This component determines which dashboard we are returning to the
 * borrower upon completion of the questionnaire. This is determined using
 * the consultPath field populated by the server when the complete
 * mutation is hit.
 * Important to note that this component is only used for the flows that have
 * the possibility of concluding with a consult.
 */
export default class QuestionnaireEvaluation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      publishedEvent: false
    };
  }

  componentDidUpdate() {
    if (this.props.location.pathname.match(/(consult)$/) && !this.state.publishedEvent) {
      const consultPath = sessionStorage.getItem('consultPath');
      const scenario = this.props.scenario;
      pipeConsultPitch(this.props.actionPipe, scenario, consultPath);

      referralRockConversion({
        fullname: this.props.name,
        email: this.props.email,
        externalidentifier: getAuthToken(),
        note: `From Legacy Questionnaire - Questionnaire: ${scenario} - Evaluation: ${consultPath}`
      });
      this.setState({ publishedEvent: true });
    }
  }

  render() {
    animateScroll.scrollToTop({
      duration: 0,
      delay: 0,
      smooth: false
    });
    const {
      acceptedOffer,
      scenario,
      consultPath,
      products,
      advisor,
      name,
      email,
      state,
      purpose,
      viewer,
      actionPipe,
      reviewItems,
      savings,
      vaStatus,
      ...props
    } = this.props;
    let { steps } = this.props;

    const { first, last } = parseName(name);

    let message = getMessage(scenario, first, savings, state, vaStatus);

    /**
     * Required to persist the consultPath too handle
     * the case when a user clicks back from another page.
     * */
    if (consultPath) {
      sessionStorage.setItem(CONSULT_CONSTANT, consultPath);
      if (products) {
        sessionStorage.setItem(
          'evaluation-data',
          JSON.stringify({
            path: consultPath,
            advisor: advisor || undefined,
            products
          })
        );
      }
    }

    if (process.env.DEPLOY_ENV === 'staging') {
      // TEST PATH FOR USER TESTING
      const testPath = sessionStorage.getItem('testPath');
      if (testPath === 'A') {
        window.location.replace(
          'https://www.figma.com/proto/5XcousxrBTbBRNdI0eXjkw/Prototype?node-id=1%3A119&viewport=625%2C430%2C0.5621480345726013&scaling=min-zoom'
        );
      } else if (testPath === 'B') {
        window.location.replace(
          'https://www.figma.com/proto/5XcousxrBTbBRNdI0eXjkw/Prototype?node-id=1%3A381&viewport=675%2C486%2C0.4242071509361267&scaling=min-zoom'
        );
      }
    }

    let inTextRatesExperiment = false;
    const authToken = getAuthToken();
    const storedPath = sessionStorage.getItem(CONSULT_CONSTANT);
    switch (storedPath) {
      case TRADELINE_ISSUE_PATH:
      case LOW_CREDIT_SCORE_PATH:
        return <LowCreditDashboard firstName={first} props={props} {...props} />;
      case NON_PITCH:
        return <NonPitchDashboard firstName={first} props={props} {...props} />;
      case TOO_FAR_OUT_PATH:
      case CAPACITY_PATH:
        return (
          <GenericDashboard
            message={`Hey ${first}, thanks for submitting your information. We're reviewing your profile and will be in touch with your next steps.`}
            articles={[
              {
                href: 'the-right-pre-approval-letter-helps-you-get-the-home-you-want',
                title: 'The Right Pre-Approval Letter Helps You Get The Home You Want'
              },
              {
                href: 'mortgage-documentation-checklist',
                title: 'The Ultimate Guide to Everything You Need for a Mortgage'
              },
              {
                href: 'decoding-mortgage-jargon',
                title: 'Decoding Mortgage Jargon'
              }
            ]}
            props={props}
            {...props}
          />
        );
      case TOO_SOON_PATH:
        return (
          <ComparisonDashboard
            actionPipe={actionPipe}
            adivsor={advisor}
            firstName={first}
            showHeader={true}
            prefill={{
              NAME: name,
              EMAIL: email,
              STATE: state,
              OEMAIL: email
            }}
            comparisonQuery={{
              firstname: first,
              last,
              email
            }}
            {...props}
          />
        );
      case NEW_CONSTRUCTION_PATH:
        return <NewConstructionDashboard firstName={first} props={props} {...props} />;
      case CASH_OUT_PATH:
        message = `Hey ${first}, you can save $${savings} annually with Own Up vs other lenders. Set up a 7 minute call to discuss your options.`;
      case CONSULT_PATH:
        if (acceptedOffer) {
          // Inform Google Optimize that we are now eligible for this experiment
          activateExperiment(EXPERIMENT_TEXT_RATES_ACTIVATE);
          // Ask Google Optimize if it wanted to put us in the experiment.
          // The returned value is a string containing the "value" given. In this case,
          //   since it's a 50/50 A/B test the values are "0" and "1" as strings.
          //   This means we can't do a truthy check!
          inTextRatesExperiment = getExperimentValue(EXPERIMENT_TEXT_RATES) === '1';
          if (inTextRatesExperiment) {
            // ... run the logic to "inform" the backend that we're now part of the experiment.
            //
            // We want to flag all hubspot deals with `rg_in_text_rates_experiment` when
            //   they are shown this text rates button. Unfortunately, because experiment
            //   grouping is done on the frontend, this means we need a backend integration
            //   to do this. As such, we have to hit a one-off endpoint to perform the
            //   deal lookup and upsert.
            //
            // We can run this asynchronusly--no need to await it.
            fetch('/api/questionnaire/text-rates', {
              headers: { ['x-rg-auth']: authToken, 'Content-Type': 'application/json' },
              method: 'POST'
            });
            actionPipe.next(event('In text rates experiment', { scenario, consultPath, savings }));
            steps = [
              'Submit basic information',
              'Connect with your advisor',
              'Receive your offers'
            ];
            message = (
              <DefaultMessageHeader>
                {first}, our best offers can save you ${savings}+ per year. Connect with your
                advisor to review your options.
              </DefaultMessageHeader>
            );
          }
        }
      default:
        return (
          <Complete
            advisor={advisor}
            scenario={scenario}
            purpose={purpose}
            prefill={{
              NAME: name,
              EMAIL: email,
              STATE: state
            }}
            state={state}
            hideAdvisor={inTextRatesExperiment}
            action={
              <ConsultDashboard
                {...{
                  textRatesExperiment: inTextRatesExperiment
                    ? {
                        name: first,
                        userId: authToken,
                        advisor: 'adam'
                      }
                    : undefined,
                  viewer,
                  actionPipe,
                  scenario,
                  purpose,
                  advisor,
                  prefill: {
                    NAME: name,
                    EMAIL: email,
                    STATE: state,
                    OEMAIL: email
                  },
                  message,
                  reviewItems
                }}
                steps={<Steps currentStep={2} steps={steps} />}
              />
            }
            actionPipe={actionPipe}
            {...props}
          />
        );
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/questionnaire-evaluation/index.js